import { Checkbox, FormControlLabel, Grid2, TextField } from "@mui/material";
import { Moment } from "moment";
import { ChangeEvent, FC, useEffect, useState } from "react";
import {
	EndFilter,
	StartFilter,
} from "../../../shared/components/StartEndFilter";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";

type Props = {
	onFilterChange: (startDate: Moment, endDate: Moment) => void;
	initialStartDate: Moment;
	initialEndDate: Moment;
	selectedDelayArea: number | null;
	onDelayAreaChange: (assetId: number | null) => void;
	includeMicrodelays: boolean;
	onIncludeMicrodelaysChange: (include: boolean) => void;
	includeUndeclaredDelays: boolean;
	onIncludeUndeclaredDelaysChange: (include: boolean) => void;
	includeRollChange: boolean;
	onIncludeRollChangeChange: (include: boolean) => void;
	thresholdPct: number;
	onThresholdPctChange: (value: number) => void;
	topN: number;
	onTopNChange: (value: number) => void;
};

export const Filters: FC<Props> = ({
	onFilterChange,
	initialStartDate,
	initialEndDate,
	selectedDelayArea,
	onDelayAreaChange,
	includeMicrodelays,
	onIncludeMicrodelaysChange,
	includeUndeclaredDelays,
	onIncludeUndeclaredDelaysChange,
	includeRollChange,
	onIncludeRollChangeChange,
	thresholdPct,
	onThresholdPctChange,
	topN,
	onTopNChange,
}) => {
	const [startDate, setStartDate] = useState<Moment>(initialStartDate);
	const [endDate, setEndDate] = useState<Moment>(initialEndDate);

	useEffect(() => {
		setStartDate(initialStartDate);
		setEndDate(initialEndDate);
	}, [initialStartDate, initialEndDate]);

	const handleStartDateChange = (date: Moment | null) => {
		if (date) {
			setStartDate(date);
			onFilterChange(date, endDate);
		}
	};

	const handleEndDateChange = (date: Moment | null) => {
		if (date) {
			setEndDate(date);
			onFilterChange(startDate, date);
		}
	};

	const onChangeDecimalType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		currentValue: number,
		onChange: (value: number) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "") {
			onChange(0);
		} else if (/^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			const numValue = parseFloat(value);
			if (!isNaN(numValue)) {
				onChange(numValue);
			}
		}
	};

	const onBlurDecimalType = (
		value: number,
		onChange: (value: number) => void,
		min?: number,
		max?: number,
	) => {
		let finalValue = value;
		if (min !== undefined && finalValue < min) finalValue = min;
		if (max !== undefined && finalValue > max) finalValue = max;
		if (finalValue !== value) {
			onChange(finalValue);
		}
	};

	const onChangeIntegerType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		currentValue: number,
		onChange: (value: number) => void,
	) => {
		const value = e.target.value;
		if (value === "") {
			onChange(0);
		} else if (/^\d+$/.test(value)) {
			const numValue = parseInt(value, 10);
			if (!isNaN(numValue)) {
				onChange(numValue);
			}
		}
	};

	const onBlurIntegerType = (
		value: number,
		onChange: (value: number) => void,
		min?: number,
		max?: number,
	) => {
		let finalValue = value;
		if (min !== undefined && finalValue < min) finalValue = min;
		if (max !== undefined && finalValue > max) finalValue = max;
		if (finalValue !== value) {
			onChange(finalValue);
		}
	};

	return (
		<Grid2 container alignItems="center" spacing={2}>
			<Grid2 size={{ xs: 12, md: 1.5 }}>
				<StartFilter value={startDate} onChange={handleStartDateChange} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 1.5 }}>
				<EndFilter value={endDate} onChange={handleEndDateChange} />
			</Grid2>
			<Grid2 size={{ xs: 12, md: 1.5 }}>
				<DelayAreaSelector
					value={selectedDelayArea}
					onChange={onDelayAreaChange}
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: "auto" }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeMicrodelays}
							color="primary"
							onChange={(e) => onIncludeMicrodelaysChange(e.target.checked)}
						/>
					}
					label="Microdelays"
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: "auto" }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeUndeclaredDelays}
							color="primary"
							onChange={(e) =>
								onIncludeUndeclaredDelaysChange(e.target.checked)
							}
						/>
					}
					label="Undeclared Delays"
				/>
			</Grid2>
			<Grid2 size={{ xs: 12, md: "auto" }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeRollChange}
							color="primary"
							onChange={(e) => onIncludeRollChangeChange(e.target.checked)}
						/>
					}
					label="Roll Changes"
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 1 }}>
				<TextField
					label="Threshold %"
					type="text"
					size="small"
					value={thresholdPct}
					onChange={(e) =>
						onChangeDecimalType(e, thresholdPct, onThresholdPctChange)
					}
					onBlur={() => onBlurDecimalType(thresholdPct, onThresholdPctChange)}
					sx={{
						backgroundColor: (theme) => theme.palette.background.paper,
					}}
				/>
			</Grid2>
			<Grid2 size={{ xs: 6, md: 1 }}>
				<TextField
					label="Top N"
					type="text"
					size="small"
					value={topN}
					onChange={(e) => onChangeIntegerType(e, topN, onTopNChange)}
					onBlur={() => onBlurIntegerType(topN, onTopNChange)}
					sx={{
						backgroundColor: (theme) => theme.palette.background.paper,
					}}
				/>
			</Grid2>
		</Grid2>
	);
};
